import React, { useRef, useState, lazy, useEffect } from 'react';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';

import PostHogHelper from '~/utils/posthog-helper';
import { blindPostRequest } from '~/utils/network';
import { fileToBase64 } from '~/utils/helpers';
import { CapCaptcha } from '../reusable/CapCaptcha';

const Marketing = lazy(() => import('../forms/marketing'));
const Review = lazy(() => import('../forms/review'));
const Contact = lazy(() => import('../forms/contact'));
const General = lazy(() => import('../forms/general'));
const GetInTouch = lazy(() => import('../forms/get-in-touch'));
const Sales = lazy(() => import('../forms/sales'));
const ClaimYourGift = lazy(() => import('../forms/claim-gift'));
const Package = lazy(() => import('../forms/package'));
// const Abuse = lazy(() => import('../forms/abuse'));
const AbuseForm = lazy(() => import('../forms/abuse-revamp'));

/**
 * I can't wait to make this file more dynamic
 * - rushing for christmas page
 * @returns react fragment with form fields
 */

const ContactFormWrapper = ({ block }) => {
  const location = useLocation();
  let contactType = 1; // 0 = contact, 1 = sales, 2 = abuse
  let contactUrl = process.env.GATSBY_API_ENDPOINT + '/contact/submit-form';
  /** Set by getWrappedForm  */
  let thankyouPageUrl = '';

  if (
    block._template === 'block__contact_form_review' ||
    block._template === 'block__contact_form_general' ||
    block._template === 'block__contact_form_get_in_touch' ||
    block._template === 'block__contact_form'
  ) {
    contactType = 0;
  }
  if (block._template === 'block__contact_form_abuse') {
    contactType = 2;
    contactUrl = process.env.GATSBY_API_ENDPOINT + '/contact/submit-form';
  }

  const randomIdPrefix =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);

  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const formRef = useRef(null);

  const [selectedCountry, setSelectedCountry] = useState('US');

  const handlePhoneCode = phoneCode => {
    setSelectedCountry(phoneCode);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const contactEmail = `${formData.get('ContactEmail')}`;

    if (block.campaign && block.campaign.length > 0) {
      formData.append('Campaign', block.campaign);
    }

    if (formData.get('FullName') === null) {
      formData.append(
        'FullName',
        formData.get('FirstName') + ' ' + formData.get('LastName')
      );
      formData.delete('FirstName');
      formData.delete('LastName');
    }

    const phoneData = formData.get('Phone');
    if (phoneData) {
      formData.delete('Phone');
      formData.append('Phone', '+' + selectedCountry + ' ' + phoneData);
    }

    const { elements } = formRef.current;
    if (elements?.Services) {
      const servicesData = [];
      elements.Services.forEach(checkbox => {
        if (checkbox.checked) {
          servicesData.push(checkbox.value);
        }
      });
      formData.set('Services', servicesData.join(','));
    }

    if (!formData.get('PowToken')) {
      setErrorData({
        success: false,
        message: 'Please verify that you are human.',
        missingFields: [],
      });
      return;
    }

    let Title = block._template
      .replace('block__contact_form', '')
      .replaceAll('_', ' ')
      .trim();
    if (Title.length <= 0) Title = 'Contact';
    Title = `${Title[0].toUpperCase()}${Title.slice(1)}`;
    formData.append('Title', Title);

    // Convert FormData to plain object
    const formBody = {};
    for (const [key, value] of formData.entries()) {
      formBody[key] = value;
    }

    // Convert screenshot file to base64 if present
    const screenshotFile = formData.get('Screenshot');
    const hasFile =
      screenshotFile instanceof File &&
      screenshotFile.size > 0 &&
      screenshotFile.name !== 'undefined';

    if (hasFile) {
      try {
        formBody['Screenshot'] = await fileToBase64(screenshotFile);
      } catch (err) {
        console.error('Failed to convert screenshot to base64', err);
        // Optionally handle fallback here
      }
    } else {
      delete formBody['Screenshot'];
    }

    blindPostRequest(contactUrl, formBody).then(async responseOk => {
      setSubmitted(true);

      if (responseOk) {
        setSuccess(true);
        await PostHogHelper.promiseToCaptureEmail(contactEmail);
        navigate(thankyouPageUrl);
      } else {
        setSuccess(false);
      }
    });
  };

  // CSS classes
  if (block.form_style === 'Centered') {
    block.style_class = ' style-centered';
  } else {
    block.style_class = ' style-basic';
  }

  if (block._template === 'block__contact_form_get_in_touch') {
    block.cols_class = 'form-cols-git';
  } else {
    block.cols_class = 'form-cols';
  }

  const getWrappedForm = block_template => {
    switch (block_template) {
      case 'block__contact_form_review':
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <Review />;
      case 'block__contact_form':
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <Contact handlePhoneCode={handlePhoneCode} />;
      case 'block__contact_form_marketing':
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <Marketing handlePhoneCode={handlePhoneCode} />;
      case 'block__contact_form_get_in_touch':
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <GetInTouch />;
      case 'block__contact_form_contact_sales':
        thankyouPageUrl = 'https://bunny.net/thank-you-sales';
        return (
          <Sales
            handlePhoneCode={handlePhoneCode}
            randomIdPrefix={randomIdPrefix}
          />
        );
      case 'block__contact_form_claim_your_gift':
        thankyouPageUrl = 'https://bunny.net/thank-you-gift';
        return <ClaimYourGift handlePhoneCode={handlePhoneCode} />;
      case 'block__contact_form_package':
        thankyouPageUrl = 'https://bunny.net/thank-you-server';
        return <Package handlePhoneCode={handlePhoneCode} />;
      case 'block__contact_form_general':
      default:
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <General />;
    }
  };

  return (
    <section
      className={`block ${block._template} ${block.css_class_name ? block.css_class_name : ''} contact-form ${block.add_form_margin ? 'form-margin' : ''} ${block.style_class ? block.style_class : ''}`}
    >
      <div className='container'>
        {block.image && (
          <img
            src={block.image}
            alt={block.image_alt_text}
            title={block.image_alt_text}
            className='bunny-no-touch'
          />
        )}
        <div className='row'>
          <div className={block.cols_class}>
            <div className='form-wr'>
              {block._template === 'block__contact_form_abuse' && (
                <AbuseForm
                  block={block}
                  includeAbuseInUrl={false}
                />
              )}
              {block._template !== 'block__contact_form_abuse' && (
                <form
                  onSubmit={handleSubmit}
                  ref={formRef}
                >
                  {block._template === 'block__contact_form_marketing' && (
                    <FormImage block={block} />
                  )}
                  {submitted && !success && (
                    <div className='error-message'>
                      There was an error submitting this contact form. Please
                      try again or send us an email at sales@bunny.net.
                    </div>
                  )}

                  <div className={`row${success ? 'is-hidden' : ''}`}>
                    {getWrappedForm(block._template)}
                    <div className='col-12 form-submit'>
                      <input
                        type='hidden'
                        name='IsValid'
                        value='false'
                      />
                      <input
                        type='hidden'
                        name='Type'
                        value={contactType}
                      />
                      <div className='form-terms'>
                        <input
                          type='checkbox'
                          name='AgreeWithTerms'
                          required
                          value='I have read and agree to the bunny.net Privacy Policy.'
                        />
                        <label htmlFor='AgreeWithTerms'>
                          I have read and agree to the bunny.net{' '}
                          <a href='/privacy'>Privacy Policy.</a>
                        </label>
                      </div>

                      <CapCaptcha className='mb-8' />
                      <button
                        type='submit'
                        name='submit'
                        className='form-action'
                        value={block.submit_label}
                      >
                        {block.submit_label}
                      </button>
                      <div
                        className='support-text'
                        dangerouslySetInnerHTML={{ __html: block.support_text }}
                      ></div>
                      {errorData?.message && (
                        <div className='error-message !mb-0 mt-2'>
                          {errorData.message}
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FormImage = ({ block }) => {
  return (
    <img
      src={block.side_image}
      alt={block.side_image_alt}
      title={block.side_image_alt}
      className='contact_form__image'
      loading='lazy'
    />
  );
};

export default ContactFormWrapper;
